//Begin Library/Built-in Imports----------------------------------------------
import React from "react";

import MasterNavbar from "./components/Navbar/MasterNavbar";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
//End Library/Built-in Imports-----------------------------------------------

//Begin Local Imports--------------------------------------------------------
import { RequireAuth } from "./components/Utils/RequireAuth";
import { LEGACY_ROUTE_ALIASES } from "./config/routes";

//End Local Imports----------------------------------------------------------

//Begin Pages Imports--------------------------------------------------------
//General Pages
import SignIn from "./pages/Access/SignIn";
import useToken from "./pages/Access/useToken";
import HomeLanding from "./pages/Home Landing/homeLanding";
/* import NotSupported from "./pages/Home Landing/NotSupport"; */
import Help from "./pages/FAQ/Help";
import "./App.css";

//Admin
import AddUser from "./pages/Admin/AddUser";
import AdminUtils from "./pages/Admin/AdminUtils";
import TrackRequisitions from "./pages/Admin/TrackRequisitions";
import ChairReportGeneration from "./pages/Admin/ChairReportGeneration";
import SPOTnotSetup from "./pages/Admin/SPOTnotSetup";
import EditReportComments from "./pages/Admin/EditReportComments";
import UpdateDDCs from "./pages/Admin/UpdateDDCs";

//Midterms
import MidtermView from "./pages/Midterm/MidtermView";
import MidtermQuestionSets from "./pages/Midterm/MidtermQuestionSets";

//Reports
import SPOTDDCSearch from "./pages/Reports/Report Search/SPOT/SPOTDDCSearch";
import USRIDDCSearch from "./pages/Reports/Report Search/USRI/USRIDDCSearch";
import MySPOTReports from "./pages/Reports/Report Results/SPOT/MySPOTReports";
import SPOTDDCReports from "./pages/Reports/Report Results/SPOT/SPOTDDCReports";
import SearchSpotReports from "./pages/Reports/Report Search/SPOT/SearchSpotReports";
import SearchSpotResults from "./pages/Reports/Report Results/SPOT/SearchSpotResults";
import ReportView from "./pages/Reports/Visual Display/ReportView";
import USRIDDCSearchResults from "./pages/Reports/Report Results/USRI/USRIDDCSearchResults";
import MyUSRIReports from "./pages/Reports/Report Results/USRI/MyUSRIReports";
import Reporting from "./pages/Reports/Reporting";
import RedactReportComments from "./pages/Reports/RedactReportComments";
import DesigneeForm from "./pages/Reports/DesigneeForm";

//Requisitions
import SPOTOrdering from "./pages/SPOTSurveys/SPOTOrdering/SPOTOrdering";
import SPOTGradesSubmitted from "./pages/SPOTSurveys/Grades Submitted/SPOTGradesSubmitted";
import SpotSurveys from "./pages/SPOTSurveys/SpotSurveys";

//Request For Service
import RequestForService from "./pages/RFS/RequestForService";
import ReqSuccess from "./pages/RFS/ReqSuccess";
import TestScoring from "./pages/RFS/TestScoring";

//User
import UserSettings from "./pages/User/UserSettings";

//Utilities
import AddBlocks from "./pages/Utilities/AddBlocks/AddBlocks";
import ViewBlocks from "./pages/Utilities/AddBlocks/ViewBlocks";
import AddItem from "./pages/Utilities/AddItems/AddItems";
import DepartmentSummary from "./pages/Utilities/DepartmentSummary/DepartmentSummary";
import EditClasses from "./pages/SPOTSurveys/EditClasses/EditClasses";
import SearchUsriResults from "./pages/Reports/Report Results/USRI/SearchUsriResults";
import ViewCatalog from "./pages/Utilities/ViewCatalog/ViewCatalog";
import ViewDDCs from "./pages/Utilities/ViewDDCs";

//Exams
import SSOCallback from "./SSOCallback";
import ExamGrading from "./pages/Exams/ExamGrading";
import ScanningCalendar from "./pages/Exams/ScanningCalendar";
import ViewGradingRequests from "./pages/RFS/ViewRequests";

//End Pages Imports-------------------------------------------------------

const queryClient = new QueryClient();

function App() {
	const [disabled, setDisabled] = React.useState(false);
	const [loginError, setLoginError] = React.useState("");
	const { token, setToken } = useToken(setDisabled, setLoginError);

	const theme = createTheme({
		palette: {
			primary: {
				light: "#007A33",
				main: "#275d38",
				dark: "#003216",
				contrastText: "#F5F5F5",
			},
			primaryGold: {
				light: "#F5E0A3",
				main: "#F2CD00",
				dark: "#D4A800",
				contrastText: "#000",
			},
			secondaryGold: {
				light: "#fcc335",
				main: "#FFB600",
				dark: "#d19500",
				contrastText: "#000",
			},
			secondaryOrange: {
				light: "#F6A600",
				main: "#F68D2E",
				dark: "#D66A00",
				contrastText: "#000",
			},
			secondaryRed: {
				light: "#F56A54",
				main: "#E56A54",
				dark: "#D56A54",
				contrastText: "#000",
			},
			secondaryPurple: {
				light: "#C86BA8",
				main: "#C86BA8",
				dark: "#A45A8B",
				contrastText: "#000",
			},
			secondaryGreen: {
				light: "#A5D6A7",
				main: "#007A33",
				dark: "#003216",
				contrastText: "#F5F5F5",
			},
			secondaryLime: {
				light: "#C6FF00",
				main: "#6CC24A",
				dark: "#4A9B2A",
				contrastText: "#000",
			},
			secondaryTeal: {
				light: "#80CBC4",
				main: "#6BBBAE",
				dark: "#4A8B8A",
				contrastText: "#000",
			},
			secondaryIndigo: {
				light: "#A7C6ED",
				main: "#7BA4DB",
				dark: "#4A7BBE",
				contrastText: "#000",
			},
			invertedPrimary: {
				light: "#fff",
				main: "#F5F5F5",
				dark: "#DEDEDE",
				contrastText: "#275d38",
			},
			greys: {
				light: "#E0E0E0",
				main: "#E0E0E0",
				dark: "#bdbdbd",
				contrastText: "#000",
			},
			navText: {
				light: "#797b7d",
				main: "##4c4c4d",
				dark: "#252526",
				contrastText: "#fff",
			},
			greysDark: {
				light: "#a6a8ab",
				main: "#929496",
				dark: "#797b7d",
				contrastText: "#fff",
			},
			mitra: {
				main: "#dda2fc",
			},
			george: {
				main: "#f74367",
			},
			pending: {
				main: "#f5c242",
			},
			classids: {
				locked: "#a6a8ab",
				unsuitable: "#f74367",
				ending: "#f7a574",
				available: "#007A33",
			},
		},

		typography: {
			fontFamily: "Roboto, sans-serif",
			button: {
				textTransform: "none",
			},
		},

		breakpoints: {
			values: {
				sm: 0,
				md: 500,
				lg: 700,
				xl: 1000,
			},
		},
	});

	// const [alerted, setAlerted] = React.useState(false);

	// if (isMobile) {
	// 	if (!alerted) {
	// 		alert(
	// 			"This application requires a minimum resolution width of 1200 pixels. If your resolution is lower than this, your experience will be heavily impacted."
	// 		);
	// 		setAlerted(true);
	// 		/* return <NotSupported />; */
	// 	}
	// }

	//if token exists and current route is /SignIn, route to /

	if (
		!token &&
		window.location.pathname !== "/SignIn" &&
		!window.location.pathname.includes("/sso/callback") &&
		window.location.pathname !== "/midterm-question-sets" &&
		window.location.pathname !== "/FAQ"
	) {
		const requestedRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
		sessionStorage.setItem("postAuthRedirect", requestedRoute);
		window.location.href = "/SignIn";
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<QueryClientProvider client={queryClient}>
					<Router>
						<MasterNavbar />

						<Routes>
							<Route
								path="/SignIn"
								element={
									<SignIn
										token={token}
										setToken={setToken}
										disabled={disabled}
										setDisabled={setDisabled}
										loginError={loginError}
									/>
								}
							/>
							<Route path="/sso/callback" element={<SSOCallback />} />
							{Object.entries(LEGACY_ROUTE_ALIASES).map(([legacyPath, canonicalPath]) => (
								<Route
									key={`legacy-${legacyPath}`}
									path={legacyPath}
									element={<Navigate to={canonicalPath} replace />}
								/>
							))}

							<Route path="/" element={<HomeLanding />} />
							<Route
								path="/test-scoring/request-for-service"
								element={
									<RequireAuth>
										<RequestForService />
									</RequireAuth>
								}
							/>
							<Route
								path="/test-scoring/success"
								element={
									<RequireAuth>
										<ReqSuccess />
									</RequireAuth>
								}
							/>

							<Route
								path="/test-scoring/view-scanning-requests"
								element={
									<RequireAuth>
										<ViewGradingRequests />
									</RequireAuth>
								}
							/>

							<Route path="/FAQ" element={<Help />} />
							<Route
								path="/reporting/SPOT-DDC-Search"
								element={
									<RequireAuth>
										<SPOTDDCSearch />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/USRI-DDC-Search"
								element={
									<RequireAuth>
										<USRIDDCSearch />
									</RequireAuth>
								}
							/>

							<Route
								path="/reporting/search-usri-reports"
								element={
									<RequireAuth>
										<SearchUsriResults />
									</RequireAuth>
								}
							/>
							<Route
								path="/spot/view-catalog"
								element={
									<RequireAuth>
										<ViewCatalog />
									</RequireAuth>
								}
							/>

							<Route
								path="/admin/Chair-Report-Generation"
								element={
									<RequireAuth>
										<ChairReportGeneration />
									</RequireAuth>
								}
							/>
							{/* <Route
								path="/admin/Edit-Report-Comments"
								element={
									<RequireAuth>
										<EditReportComments />
									</RequireAuth>
								}
							/> */}
							<Route
								path="/admin/Update-DDCs"
								element={
									<RequireAuth>
										<UpdateDDCs />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/Redact-Report-Comments"
								element={
									<RequireAuth>
										<RedactReportComments />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/DDC-Designee-Form"
								element={
									<RequireAuth>
										<DesigneeForm />
									</RequireAuth>
								}
							/>
							<Route
								path="/admin/Courses-Without-SPOTs"
								element={
									<RequireAuth>
										<SPOTnotSetup />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/My-SPOT-Reports"
								element={
									<RequireAuth>
										<MySPOTReports />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/My-USRI-Reports"
								element={
									<RequireAuth>
										<MyUSRIReports />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/SPOT-DDC-Reports"
								element={
									<RequireAuth>
										<SPOTDDCReports />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/USRI-DDC-Search-Results"
								element={
									<RequireAuth>
										<USRIDDCSearchResults />
									</RequireAuth>
								}
							/>
							<Route
								path="/spot"
								element={
									<RequireAuth>
										<SpotSurveys />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/Search-SPOT-Results"
								element={
									<RequireAuth>
										<SearchSpotResults />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/search-spot-reports"
								element={
									<RequireAuth>
										<SearchSpotReports />
									</RequireAuth>
								}
							/>

							<Route
								path="/admin/TrackRequisitions"
								element={
									<RequireAuth>
										<TrackRequisitions />
									</RequireAuth>
								}
							/>
							<Route
								path="/utilities/AddUser"
								element={
									<RequireAuth>
										<AddUser />
									</RequireAuth>
								}
							/>
							<Route
								path="/admin/AdminUtils"
								element={
									<RequireAuth>
										<AdminUtils />
									</RequireAuth>
								}
							/>

							<Route
								path="/spot/spot-ordering"
								element={
									<RequireAuth>
										<SPOTOrdering />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting"
								element={
									<RequireAuth>
										<Reporting />
									</RequireAuth>
								}
							/>
							<Route
								path="/test-scoring"
								element={
									<RequireAuth>
										<TestScoring />
									</RequireAuth>
								}
							/>

							<Route
								path="/spot/spot-report-distribution"
								element={
									<RequireAuth>
										<SPOTGradesSubmitted />
									</RequireAuth>
								}
							/>

							<Route
								path="/utilities/EditClasses"
								element={
									<RequireAuth>
										<EditClasses />
									</RequireAuth>
								}
							/>

							<Route
								path="/utilities/ViewDDCs"
								element={
									<RequireAuth>
										<ViewDDCs />
									</RequireAuth>
								}
							/>

							<Route
								path="/spot/view-blocks"
								element={
									<RequireAuth>
										<ViewBlocks />
									</RequireAuth>
								}
							/>
							<Route
								path="/spot/spot-dept-summary"
								element={
									<RequireAuth>
										<DepartmentSummary />
									</RequireAuth>
								}
							/>

							<Route path="/midterm-question-sets" element={<MidtermQuestionSets />} />
							<Route
								path="/UserSettings"
								element={
									<RequireAuth>
										<UserSettings />
									</RequireAuth>
								}
							/>

							<Route
								path="/test-scoring/exam-grading"
								element={
									<RequireAuth>
										<ExamGrading />
									</RequireAuth>
								}
							/>
							<Route
								path="/admin/scanning-calendar"
								element={
									<RequireAuth>
										<ScanningCalendar />
									</RequireAuth>
								}
							/>
							<Route
								path="/reporting/Report-View"
								element={
									<RequireAuth>
										<ReportView />
									</RequireAuth>
								}
							/>
							<Route
								path="/admin/MidtermView"
								element={
									<RequireAuth>
										<MidtermView />
									</RequireAuth>
								}
							/>
							<Route
								path="/utilities/AddItem"
								element={
									<RequireAuth>
										<AddItem />
									</RequireAuth>
								}
							/>
							<Route
								path="/utilities/AddBlocks"
								element={
									<RequireAuth>
										<AddBlocks />
									</RequireAuth>
								}
							/>
						</Routes>
					</Router>

					<ReactQueryDevtools initialIsOpen={false} />
				</QueryClientProvider>
			</ThemeProvider>
		</LocalizationProvider>
	);
}

export default App;
